import { Link, useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import { History } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StarButton } from '@/components/StarButton';
import { useAuthor } from '@/hooks/useAuthor';
import { useWikiVersions } from '@/hooks/useWikiArticles';
import { normalizeWikiTitle, prettifySlug } from '@/lib/wikiSlug';
import { articleTitle, WIKI_ARTICLE_KIND } from '@/lib/nip54';
import NotFound from '@/pages/NotFound';

export function ArticleHistoryPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slug = rawSlug ? normalizeWikiTitle(decodeURIComponent(rawSlug)) : undefined;
  const { data: versions, isLoading } = useWikiVersions(slug);

  useSeoMeta({
    title: slug ? `History of ${prettifySlug(slug)} — Wikistar` : 'Wikistar',
  });

  if (!slug) return <NotFound />;

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold tracking-tight break-words">
          <History className="size-6 shrink-0" />
          Versions of "{prettifySlug(slug)}"
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Every author who has written an article under this title. Nostr wiki articles aren't
          merged automatically — each version is independent and shown side by side.
        </p>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : versions && versions.length > 0 ? (
          <div className="space-y-3">
            {versions.map((event) => (
              <VersionRow key={event.pubkey} slug={slug} event={event} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">No versions found for this article yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

function VersionRow({ slug, event }: { slug: string; event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const displayName = author.data?.metadata?.name ?? `${event.pubkey.slice(0, 8)}…`;
  const npub = nip19.npubEncode(event.pubkey);

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <Link to={`/${npub}`} className="flex min-w-0 items-center gap-3 hover:underline">
          <Avatar className="shrink-0">
            <AvatarImage src={author.data?.metadata?.picture} alt={displayName} />
            <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(event.created_at * 1000).toLocaleString()}
            </p>
          </div>
        </Link>
        <div className="flex min-w-0 items-center gap-3">
          <StarButton event={event} addressPointer={`${WIKI_ARTICLE_KIND}:${event.pubkey}:${slug}`} />
          <Link
            to={`/w/${encodeURIComponent(slug)}/${npub}`}
            className="min-w-0 truncate text-sm text-primary underline underline-offset-2 hover:no-underline"
          >
            View "{articleTitle(event)}"
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
